<template>
  <div class="interview-step">
    <h2 class="step-title">A Few Questions</h2>
    <p class="step-sub">Aida wants to understand who you are before she shapes your mystic identity.</p>

    <div class="questions">
      <!-- Q1 -->
      <div class="question-block" v-if="currentQ >= 1">
        <div class="aida-intro" v-if="currentQ === 1">
          <span class="aida-avatar">&#10022;</span>
          <span class="aida-text">Before we begin, I'd love to know a little about you.</span>
        </div>
        <label class="q-label">Who do you usually help?</label>
        <textarea
          v-model="answers.q1"
          class="q-input"
          placeholder="People navigating transitions, new mothers, entrepreneurs..."
          rows="3"
          :disabled="currentQ > 1"
          @focus="emit('context-signal', { panel: 'studio', view: 'interview_step', interaction: 'q1_focused', data: {} })"
          @keydown.enter.exact.prevent="advanceFromQ(1)"
        />
        <Transition name="fade">
          <div v-if="reflections.r1" class="aida-reflection">
            <span class="aida-avatar">&#10022;</span>
            <span>{{ reflections.r1 }}</span>
          </div>
        </Transition>
      </div>

      <!-- Q2 -->
      <Transition name="fade">
        <div class="question-block" v-if="currentQ >= 2">
          <label class="q-label">What's the thing people thank you for the most?</label>
          <textarea
            v-model="answers.q2"
            class="q-input"
            placeholder="Holding space without judgment, asking the right question..."
            rows="3"
            :disabled="currentQ > 2"
            @focus="emit('context-signal', { panel: 'studio', view: 'interview_step', interaction: 'q2_focused', data: {} })"
            @keydown.enter.exact.prevent="advanceFromQ(2)"
          />
          <Transition name="fade">
            <div v-if="reflections.r2" class="aida-reflection">
              <span class="aida-avatar">&#10022;</span>
              <span>{{ reflections.r2 }}</span>
            </div>
          </Transition>
        </div>
      </Transition>

      <!-- Q3 -->
      <Transition name="fade">
        <div class="question-block" v-if="currentQ >= 3">
          <label class="q-label">When someone leaves feeling exactly right -- what does that feel like for them?</label>
          <textarea
            v-model="answers.q3"
            class="q-input"
            placeholder="They feel seen. Like the weight lifted. Like they finally have a direction..."
            rows="3"
            @focus="emit('context-signal', { panel: 'studio', view: 'interview_step', interaction: 'q3_focused', data: {} })"
            @keydown.enter.exact.prevent="advanceFromQ(3)"
          />
          <Transition name="fade">
            <div v-if="reflections.r3" class="aida-reflection">
              <span class="aida-avatar">&#10022;</span>
              <span>{{ reflections.r3 }}</span>
            </div>
          </Transition>
        </div>
      </Transition>
    </div>

    <div class="step-actions" v-if="canAdvance">
      <button class="primary-btn" @click="advance">
        {{ currentQ === 3 && answers.q3.trim() ? 'Complete \u2192' : 'Next \u2192' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'

const emit = defineEmits(['interview-complete', 'context-signal'])

const currentQ = ref(1)
const answers = reactive({ q1: '', q2: '', q3: '' })
const reflections = reactive({ r1: '', r2: '', r3: '' })

// Static V1 reflections
const REFLECTIONS = {
  r1: "That tells me a lot about where your gift lives. Two more questions.",
  r2: "That's the thread I'll weave into your identity. One last question.",
  r3: "Beautiful. I have everything I need to begin.",
}

const canAdvance = computed(() => {
  if (currentQ.value === 1) return answers.q1.trim().length > 5
  if (currentQ.value === 2) return answers.q2.trim().length > 5
  if (currentQ.value === 3) return answers.q3.trim().length > 5
  return false
})

function advanceFromQ() {
  if (!canAdvance.value) return
  advance()
}

function advance() {
  if (currentQ.value === 1 && answers.q1.trim()) {
    reflections.r1 = REFLECTIONS.r1
    emit('context-signal', { panel: 'studio', view: 'interview_step', interaction: 'q1_answered', data: {} })
    setTimeout(() => { currentQ.value = 2 }, 600)
  } else if (currentQ.value === 2 && answers.q2.trim()) {
    reflections.r2 = REFLECTIONS.r2
    emit('context-signal', { panel: 'studio', view: 'interview_step', interaction: 'q2_answered', data: {} })
    setTimeout(() => { currentQ.value = 3 }, 600)
  } else if (currentQ.value === 3 && answers.q3.trim()) {
    reflections.r3 = REFLECTIONS.r3
    setTimeout(() => {
      emit('interview-complete', { q1: answers.q1, q2: answers.q2, q3: answers.q3 })
    }, 800)
  }
}
</script>

<style scoped>
.interview-step { color: #E8D8FF; }
.step-title {
  font-family: 'Cinzel', serif; font-size: 24px; font-weight: 600; color: #FFDFA3;
  margin: 0 0 8px; padding: 0; border: none; border-radius: 0;
  background: none; box-shadow: none; text-align: left;
}
.step-sub { font-size: 13px; color: rgba(255,255,255,0.45); line-height: 1.65; margin-bottom: 24px; }
.questions { display: flex; flex-direction: column; gap: 20px; }
.question-block { display: flex; flex-direction: column; gap: 8px; }
.aida-intro {
  display: flex; align-items: center; gap: 8px; font-size: 13px;
  color: rgba(125,232,204,0.8); margin-bottom: 4px;
}
.aida-avatar { font-size: 16px; color: #7DE8CC; flex-shrink: 0; }
.aida-text { font-style: italic; }
.q-label { font-size: 14px; color: rgba(232,216,255,0.8); font-weight: 500; }
.q-input {
  width: 100%; background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12); border-radius: 8px;
  padding: 12px; color: #E8D8FF; font-size: 13px; font-family: inherit;
  resize: vertical; box-sizing: border-box;
}
.q-input:focus { outline: none; border-color: rgba(125,232,204,0.4); }
.q-input:disabled { opacity: 0.5; cursor: not-allowed; }
.aida-reflection {
  display: flex; align-items: flex-start; gap: 8px; font-size: 12px;
  color: rgba(125,232,204,0.7); font-style: italic; padding: 8px 0;
}
.step-actions { margin-top: 24px; }
.primary-btn {
  width: 100%; padding: 14px;
  background: rgba(50,192,158,0.12); border: 1px solid rgba(50,192,158,0.35);
  border-radius: 10px; color: #7DE8CC; font-size: 15px; font-weight: 500;
  cursor: pointer; font-family: inherit; transition: all 0.2s;
}
.primary-btn:hover { background: rgba(50,192,158,0.22); }

.fade-enter-active { transition: opacity 0.5s ease, transform 0.5s ease; }
.fade-enter-from { opacity: 0; transform: translateY(8px); }
.fade-enter-to { opacity: 1; transform: translateY(0); }
</style>
